import React from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyCrisprMode({ brand, today, Copy }: ModeRendererProps) {
  // CRISPR gradient: DNA blue to edit green
  const crisprGradient = (
    <defs>
      <linearGradient id="crisprGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#4A90E2" />
        <stop offset="50%" stopColor="#7CB342" />
        <stop offset="100%" stopColor="#FFA726" />
      </linearGradient>
    </defs>
  );

  // Base-pair tiles: ATCG sequence
  const basePairTiles: JSX.Element[] = [];
  const bases = ["A", "T", "C", "G"];
  for (let row = 0; row < 16; row++) {
    for (let col = 0; col < 12; col++) {
      const x = 80 + col * 55;
      const y = 120 + row * 55;
      const base = bases[(row * col) % 4];
      const color = base === "A" ? "#4A90E2" : base === "T" ? "#7CB342" : base === "C" ? "#FFA726" : "#E57373";
      basePairTiles.push(
        <g key={`base-${row}-${col}`}>
          <rect
            x={x - 18}
            y={y - 18}
            width={36}
            height={36}
            fill={color}
            opacity={0.3}
            rx={4}
          />
          <text
            x={x}
            y={y + 5}
            fontFamily="monospace"
            fontSize={14}
            fontWeight="bold"
            fill={color}
            textAnchor="middle"
            opacity={0.6}
          >
            {base}
          </text>
        </g>
      );
    }
  }

  // Edit masks: CRISPR cut sites
  const editMasks: JSX.Element[] = [];
  for (let i = 0; i < 8; i++) {
    const y = 180 + i * 105;
    editMasks.push(
      <g key={`edit-${i}`}>
        <line
          x1={100}
          y1={y}
          x2={694}
          y2={y}
          stroke="#7CB342"
          strokeWidth={3}
          opacity={0.6}
          strokeDasharray="12,8"
        />
        {/* Scissors symbol */}
        <circle cx={397} cy={y} r={12} fill="none" stroke="#FFA726" strokeWidth={2} />
        <line x1={391} y1={y - 6} x2={403} y2={y + 6} stroke="#FFA726" strokeWidth={2} />
        <line x1={391} y1={y + 6} x2={403} y2={y - 6} stroke="#FFA726" strokeWidth={2} />
      </g>
    );
  }

  // Double-helix lattices: DNA structure
  const doubleHelixLattices: JSX.Element[] = [];
  for (let i = 0; i < 32; i++) {
    const angle = (i * Math.PI * 2) / 32;
    const radius = 200;
    const x1 = 397 + radius * Math.cos(angle);
    const y1 = 561.5 + radius * Math.sin(angle) * 0.3;
    const x2 = 397 + radius * Math.cos(angle + Math.PI);
    const y2 = 561.5 + radius * Math.sin(angle + Math.PI) * 0.3;
    doubleHelixLattices.push(
      <line
        key={`helix-${i}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="url(#crisprGrad)"
        strokeWidth={1}
        opacity={0.3}
      />
    );
  }

  // Helix backbone
  const helixBackbone: JSX.Element[] = [];
  const points1 = [];
  const points2 = [];
  for (let i = 0; i < 48; i++) {
    const angle = (i * Math.PI * 2) / 48;
    const y = 100 + i * 19;
    const x1 = 397 + 100 * Math.cos(angle);
    const x2 = 397 - 100 * Math.cos(angle);
    points1.push(`${x1},${y}`);
    points2.push(`${x2},${y}`);
  }
  helixBackbone.push(
    <polyline
      key="helix1"
      points={points1.join(" ")}
      fill="none"
      stroke="#4A90E2"
      strokeWidth={2}
      opacity={0.5}
    />
  );
  helixBackbone.push(
    <polyline
      key="helix2"
      points={points2.join(" ")}
      fill="none"
      stroke="#7CB342"
      strokeWidth={2}
      opacity={0.5}
    />
  );

  // Central CRISPR-Cas9 complex
  const crisprCore = (
    <g opacity={0.9}>
      <circle cx={397} cy={561.5} r={80} fill="none" stroke="#7CB342" strokeWidth={3} />
      <circle cx={397} cy={561.5} r={55} fill="none" stroke="#4A90E2" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={35} fill="none" stroke="#FFA726" strokeWidth={2} />
      <text
        x={397}
        y={575}
        fontFamily="monospace"
        fontSize={24}
        fontWeight="bold"
        fill="#7CB342"
        textAnchor="middle"
      >
        Cas9
      </text>
    </g>
  );

  return (
    <g>
      {crisprGradient}
      {basePairTiles}
      {helixBackbone}
      {doubleHelixLattices}
      {editMasks}
      {crisprCore}
      <g className="mode-copy" transform="translate(48, 840)" fill="#FFFFFF" fontFamily="monospace">
        <text x={0} y={0} fontSize={18} fontWeight="bold">{Copy?.title}</text>
        <text x={0} y={22} fontSize={14}>{Copy?.subtitle}</text>
        <text x={0} y={44} fontSize={12} opacity={0.85}>{Copy?.date}</text>
      </g>
    </g>
  );
}
